// src/config/planoConfig.js — FONTE ÚNICA DE ENTITLEMENTS (client)
//
// Mesma filosofia do muscleMapConfig.js: nenhum `if (isPremium)` solto no app —
// tudo lê deste config. Os NÚMEROS são autoritativos no SERVER (functions/utils/
// planoConfig.js); esta cópia client serve a UX (mostrar restante, decidir blur).
// Manter os dois em sincronia ao alterar limites.
//
// DUAS DIMENSÕES (30/09/2026), espelho de functions/utils/planoConfig.js:
//   tier do USUÁRIO  : GRATUITO | PREMIUM (paga) | CRIADOR
//   tier do PARCEIRO : nenhum | VINCULADO (academia Essencial) |
//                      VINCULADO_PREMIUM (academia Premium — a academia paga)
// Tier efetivo = o melhor dos dois:
//   CRIADOR > PREMIUM > VINCULADO_PREMIUM > VINCULADO > GRATUITO
// VINCULADO = modo essencial (limites do grátis, com anúncios). O que a academia
// dá a mais chega em USER.PARCEIRO.BENEFICIOS (espelho escrito pelo servidor).
export const TIERS = {
  GRATUITO: "GRATUITO",
  PREMIUM: "PREMIUM",
  VINCULADO: "VINCULADO",
  VINCULADO_PREMIUM: "VINCULADO_PREMIUM",
  CRIADOR: "CRIADOR",
};

export const PRECEDENCIA_TIER = [
  TIERS.CRIADOR, TIERS.PREMIUM, TIERS.VINCULADO_PREMIUM, TIERS.VINCULADO, TIERS.GRATUITO,
];

// Tiers que têm o pacote pago (sem anúncio, recursos liberados).
export const TIERS_PAGOS = [TIERS.PREMIUM, TIERS.VINCULADO_PREMIUM, TIERS.CRIADOR];

export const JANELAS = {
  DIA_BRT: "DIA_BRT", // dia-calendário America/Sao_Paulo (reset 00:00 BRT)
  ROLLING_30D: "ROLLING_30D", // janela deslizante: últimos 30 dias
  ROLLING_90D: "ROLLING_90D", // janela deslizante: últimos 90 dias
  VIDA: "VIDA", // uma vez na vida, contado desde VIDA_DESDE_MS
};
export const VIDA_DESDE_MS = Date.UTC(2026, 8, 30, 3, 0, 0); // 30/09/2026 00:00 BRT

// limite: null => ILIMITADO para o tier.
// Tabela vigente (30/09/2026) — manter em sincronia com
// functions/utils/planoConfig.js (autoridade).
const q = (limite, janela) => ({ limite, janela });
export const QUOTAS = {
  scanbody: {
    GRATUITO: q(1, JANELAS.ROLLING_90D),
    VINCULADO: q(1, JANELAS.ROLLING_90D),
    PREMIUM: q(3, JANELAS.ROLLING_30D),
    VINCULADO_PREMIUM: q(3, JANELAS.ROLLING_30D),
    CRIADOR: q(5, JANELAS.ROLLING_30D),
  },
  scanfood: {
    GRATUITO: q(2, JANELAS.DIA_BRT),
    VINCULADO: q(2, JANELAS.DIA_BRT),
    PREMIUM: q(6, JANELAS.DIA_BRT),
    VINCULADO_PREMIUM: q(6, JANELAS.DIA_BRT),
    CRIADOR: q(10, JANELAS.DIA_BRT),
  },
  geradorTreino: {
    GRATUITO: q(1, JANELAS.VIDA),
    VINCULADO: q(1, JANELAS.VIDA),
    PREMIUM: q(3, JANELAS.ROLLING_30D),
    VINCULADO_PREMIUM: q(3, JANELAS.ROLLING_30D),
    CRIADOR: { limite: null },
  },
  geradorDieta: {
    GRATUITO: q(1, JANELAS.VIDA),
    VINCULADO: q(1, JANELAS.VIDA),
    PREMIUM: q(3, JANELAS.ROLLING_30D),
    VINCULADO_PREMIUM: q(3, JANELAS.ROLLING_30D),
    CRIADOR: { limite: null },
  },
  registroTreino: {
    GRATUITO: { limite: null },
    VINCULADO: { limite: null },
    PREMIUM: { limite: null },
    VINCULADO_PREMIUM: { limite: null },
    CRIADOR: { limite: null },
  },
};

// Gamificação por tier — só para a UI dizer os números certos (quem credita é
// o servidor). MOEDA_MULT combina com a ofensiva: tier + ofensiva − 0,5.
export const GAMIFICACAO = {
  XP_MULT: { GRATUITO: 1, VINCULADO: 1, PREMIUM: 2, VINCULADO_PREMIUM: 2, CRIADOR: 2.5 },
  MOEDA_MULT: { GRATUITO: 1, VINCULADO: 1, PREMIUM: 1.5, VINCULADO_PREMIUM: 1.5, CRIADOR: 1.5 },
  CONGELAMENTOS: {
    GRATUITO: { LIMITE: 1, PERIODO: "MES" },
    VINCULADO: { LIMITE: 1, PERIODO: "MES" },
    PREMIUM: { LIMITE: 2, PERIODO: "SEMANA" },
    VINCULADO_PREMIUM: { LIMITE: 2, PERIODO: "SEMANA" },
    CRIADOR: { LIMITE: 2, PERIODO: "SEMANA" },
  },
};

// Camada de TICKET (bônus aditivo) lida de campo do doc USER.
// scanfood NÃO tem campo aqui: os tickets dele são COMPRADOS e vivem em
// TICKETS_SALDO (ver TICKET_SALDO_TIPO abaixo). Treino e dieta dividem o
// PLANO_TICKET (um ticket = um artefato).
export const TICKET_FIELD = {
  scanbody: "EXAME_TICKET",
  geradorTreino: "PLANO_TICKET",
  geradorDieta: "PLANO_TICKET",
};

// Compartilhamento nas redes (refeição ou treino) — espelho de
// functions/utils/planoConfig.js GAMIFICACAO.COMPARTILHAMENTO (autoridade).
// Serve só para a UI dizer os números certos; quem credita é o servidor.
export const COMPARTILHAMENTO = {
  XP: 15,
  PONTOS: 15,
  MULTIPLICADOR_VERIFICADO: 3, // verificado no painel (marcou + segue) → 3×
  CAP_DIA: 1,
};

// Tickets COMPRADOS (Pacote de Tickets Extra — crédito via webhook RevenueCat)
// em USER/{uid}/TICKETS_SALDO/{tipo}.COMPRADO_SALDO. Entram como ÚLTIMO recurso
// na precedência (janela → bônus rewarded → comprado). Manter em sincronia com
// functions/utils/planoConfig.js (autoridade — lá o gate consome de verdade).
export const TICKET_SALDO_TIPO = {
  scanfood: "REFEICAO",
};

// Teto DIÁRIO (dia BRT) de tickets COMPRADOS consumíveis. Espelho de
// functions/utils/planoConfig.js TICKET_EXTRAS_MAX_DIA (autoridade), que por
// sua vez espelha REFEICAO_EXTRAS_MAX_DIA da competição. Sem este corte a tela
// mostrava "8 tickets" e o gate/competição só honravam 6 — o usuário escaneava
// e só depois descobria que não pontuou.
export const TICKET_EXTRAS_MAX_DIA = {
  scanfood: 6,
};

// Onde cada consumo é registrado (compute-on-read) — usado para "restante" na UX.
// Caminho relativo a PACIENTE/{uid}; tsField = campo de server timestamp.
export const LOG_SOURCES = {
  scanbody: { collection: ["analises_fisicas"], tsField: "info_analise.data" },
  scanfood: { collection: ["REFEICAO_ANALISE"], tsField: "createdAt" },
  geradorTreino: {
    collection: ["PLANO_NUTRICIONAL", "CURRENT", "TREINO"],
    tsField: "CREATED_AT",
  },
  geradorDieta: {
    collection: ["PLANO_NUTRICIONAL", "CURRENT", "DIETA"],
    tsField: "CREATED_AT",
  },
};

export const FONTE_QUOTA = {
  JANELA: "JANELA",
  TICKET: "TICKET",
  BONUS: "BONUS",
  ILIMITADO: "ILIMITADO",
};

// Flags de feature visual/experiência. A academia pode sobrescrever para os
// alunos dela (USER.PARCEIRO.BENEFICIOS.FEATURES / .ADS).
const liberado = (gratis) => ({
  GRATUITO: gratis, VINCULADO: gratis,
  PREMIUM: "LIBERADO", VINCULADO_PREMIUM: "LIBERADO", CRIADOR: "LIBERADO",
});
export const FEATURES = {
  mapaMuscularInteligente: liberado("BLOQUEADO"),
  graficosEvolucao: liberado("BLOQUEADO"),
  analiseInteligente: liberado("BLOQUEADO"),
  ads: {
    GRATUITO: true, VINCULADO: true, PREMIUM: false, VINCULADO_PREMIUM: false, CRIADOR: false,
  },
};
